import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Alert, Button, Card, Field, Page, PageHeader } from '@evogard/ui';
import type { ConfigurableKey } from '@evogard/contract';
import { SENDGRID_SETTING_PREFIX, canAccess } from '@evogard/domain';
import { api } from '../../lib/api.js';
import { errorMessage } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { SETTINGS_CARDS } from '../../settings.js';

/**
 * A chave que é de uma integração — os templates da CAF (`caf.*`) e o
 * remetente do SendGrid (`sendgrid.*`) — aparece no painel dela, em
 * Integrações, e não aqui: o mesmo valor em duas telas é a segunda cópia que a
 * casa evita.
 */
export const isIntegrationSetting = (key: string) =>
  key.startsWith('caf.') || key.startsWith(SENDGRID_SETTING_PREFIX);

/**
 * O que muda de uma praça para a outra e não é código.
 *
 * ## O formulário é desenhado pelo servidor
 *
 * Esta tela não sabe quais chaves existem: ela pinta o que
 * `GET /platform/tenant-settings` devolver, com o rótulo, a ajuda e o
 * dono que vieram junto. O catálogo mora em `settings.catalog.ts`, ao lado
 * da validação — e é isso que impede o rótulo daqui de divergir do que a rota
 * aceita, que é o defeito que apareceria na terceira chave, não na primeira.
 *
 * Acrescentar uma chave, portanto, é editar **um** arquivo no servidor. Esta
 * tela não muda.
 *
 * ## Uma chave só, e a tela diz isso
 *
 * `plataforma-38` nomeia "marca, rótulos e domínio próprio". Nasce com a sigla,
 * pela regra 14: o que não dá para assumir fica de fora, inteiro, e a tela diz
 * que não faz aquilo — em vez de oferecer um campo de domínio que não roteia
 * nada e um seletor de cor que não pinta nada.
 *
 * ## Por que mostrar o dono de cada chave
 *
 * A armadilha do chave-valor é a gaveta: seis meses e doze chaves depois,
 * ninguém sabe quem lê cada uma, e apagar qualquer uma vira risco que ninguém
 * aceita correr. O dono na tela é o que mantém a pergunta respondível — e o que
 * torna visível, para quem for acrescentar a próxima, que chave sem dono não
 * entra.
 */
export function TenantSettingsScreen() {
  const { data: context } = useSessionContext();
  const client = useQueryClient();
  const tenantId = context?.currentTenant.id;

  const canEdit = canAccess(context?.permissions ?? [], 'platform.tenants', 'editar');

  const query = useQuery({
    // a base na chave: trocar de praça troca a configuração que está na tela
    queryKey: ['plataforma', 'configuracao-da-base', tenantId],
    queryFn: async () => {
      const r = await api.platform.listSetting();
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível carregar.'));
      }
      return r.body.keys;
    },
    enabled: tenantId !== undefined,
  });

  return (
    <Page measure="cheia">
      <PageHeader title="Configuração da base">
        Vale para{' '}
        <strong className="font-medium text-texto">{context?.currentTenant.nome}</strong>{' '}
        e para todo mundo que trabalha nela — não é preferência sua.
      </PageHeader>

      {query.isError && (
        <Alert tone="erro" title="Não foi possível carregar a configuração.">
          {query.error.message}
        </Alert>
      )}

      {/* Os templates da CAF moram no painel dela, em Integrações (16/10/2026). */}
      {query.data
        ?.filter((s) => !isIntegrationSetting(s.key))
        .map((setting) => (
          <SettingField
            key={setting.key}
            setting={setting}
            canEdit={canEdit}
            toWrite={(keys) => {
              client.setQueryData(['plataforma', 'configuracao-da-base', tenantId], keys);
              /*
               * O contexto também: a sigla que a moldura pinta vem de lá, e sem
               * isto a tela mostraria o valor novo com o rail ainda no antigo —
               * dois lugares afirmando siglas diferentes na mesma janela.
               */
              void client.invalidateQueries({ queryKey: ['plataforma', 'contexto'] });
            }}
          />
        ))}

      {/*
       * OS CARTÃO DE OUTROS MÓDULOS, e é por isso que esta tela não os conhece.
       *
       * A taxa por boleto da bonificação chegou aqui em 16/09/2026 — ela vale
       * para a praça inteira e não coube dentro de nenhum consultor, que é onde
       * as premiações passaram a morar. Importá-la direto **não compila**:
       * módulo não importa de módulo, e uma tela da plataforma que conhece o
       * cadastro do comercial é o começo de uma que conhece os sete.
       *
       * `SETTINGS_CARDS` é a mesma saída de `panels.ts` no início. Cartão novo
       * é uma linha lá, e nenhuma delas encosta neste arquivo.
       *
       * Eles vêm DEPOIS das chaves do catálogo e ANTES do que ainda não está
       * aqui: as chaves são o assunto da tela, e o bloco de baixo fala sobre
       * ela.
       */}
      {SETTINGS_CARDS.filter((card) =>
        card.resource === undefined || card.action === undefined
          ? true
          : canAccess(context?.permissions ?? [], card.resource, card.action),
      ).map((card) => (
        <card.component key={card.key} />
      ))}

      <Card
        title="O que ainda não está aqui"
        descricao="Escrito porque a ausência não se explica sozinha."
      >
        <p className="text-sm text-texto-suave">
          Domínio próprio e cor da marca ainda não são configuráveis. O domínio quem
          decide é o roteamento do servidor, e guardar o valor aqui sem ninguém que o leia
          seria uma gaveta; a cor exige decidir antes como um token do tema passa a ser
          dado por base. Quando cada uma tiver quem a leia, ela aparece nesta tela.
        </p>
      </Card>
    </Page>
  );
}

/**
 * Um campo, com o botão ao lado do campo — nunca no lugar dele.
 *
 * O erro aparece embaixo do campo e some na tentativa seguinte. A versão que
 * troca o controle pela mensagem tira da pessoa a única coisa que ela pode
 * fazer a respeito, e obriga a recarregar a página para recuperá-la.
 */
export function SettingField({
  setting,
  canEdit,
  toWrite,
}: {
  setting: ConfigurableKey;
  canEdit: boolean;
  toWrite: (keys: ConfigurableKey[]) => void;
}) {
  const initial = typeof setting.value === 'string' ? setting.value : '';
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  /*
   * O servidor manda no valor do campo.
   *
   * Sem isto, trocar de base deixaria no campo o texto digitado para a base
   * anterior — o componente é o mesmo, só a `key` da lista muda, e ela não muda
   * quando a chave é a mesma nas duas praças.
   */
  useEffect(() => {
    setValue(initial);
  }, [initial]);

  const write = useMutation({
    mutationFn: async () => {
      const r = await api.platform.writeSetting({
        body: { key: setting.key, value },
      });
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível salvar.'));
      }
      return r.body.keys;
    },
    onSuccess: (keys) => {
      setError(null);
      toWrite(keys);
    },
    onError: (e: Error) => setError(e.message),
  });

  const changed = value !== initial;

  return (
    <Card title={setting.label} descricao={setting.help}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null); // a tentativa nova começa sem o erro da anterior
          write.mutate();
        }}
      >
        <Field
          label={setting.label}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!canEdit}
          error={error}
        />

        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-texto-suave">
            Lida por <span className="font-medium text-texto">{setting.owner}</span> ·{' '}
            <code className="font-mono">{setting.key}</code>
          </p>

          {canEdit && (
            <Button type="submit" loading={write.isPending} disabled={!changed}>
              Salvar
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
}
